import { PRINT_FONT_X_HEIGHT_RATIO } from './layout-v2/layout-solver';

/**
 * Il marchio di identificazione dell'all. II sez. I del Reg. (CE) 853/2004.
 *
 * NON e' il bollo sanitario: quello lo appone il veterinario ufficiale sulla
 * carcassa (Reg. di esecuzione (UE) 2019/627 art. 48 e all. II) ed e' l'altro
 * ramo, alternativo, dell'art. 5 §1. Le misure 6,5 x 4,5 cm che circolano
 * appartengono al bollo, non a questo marchio.
 *
 * Il regolamento NON prescrive alcuna dimensione per il marchio di
 * identificazione: chiede che sia ovale, leggibile, indelebile e con caratteri
 * facilmente decifrabili (parte B punto 5). Il pavimento qui sotto e' quindi
 * una scelta di prodotto, non una misura di legge.
 */

/** Sigle correnti, una per lingua ufficiale (parte B punto 8, formulazione del
 *  Reg. delegato (UE) 2024/1141). */
export const IDENTIFICATION_MARK_UNION_ABBREVIATIONS = [
  'EC',
  'EU',
  'EL',
  'UE',
  'EE',
  'AE',
  'ES',
  'EÚ',
] as const;

/** Sigle anteriori al 2024, tollerate fino al 31 dicembre 2028
 *  (art. 2 Reg. delegato (UE) 2024/1141). */
export const IDENTIFICATION_MARK_LEGACY_ABBREVIATIONS = [
  'CE',
  'EC',
  'EF',
  'EG',
  'EK',
  'EO',
  'EY',
  'ES',
  'EÜ',
  'EB',
  'EZ',
  'WE',
] as const;

export const IDENTIFICATION_MARK_LEGACY_ACCEPTED_UNTIL = '2028-12-31';

/** Sigla stampata su un'etichetta italiana. */
export const IDENTIFICATION_MARK_PRINTED_ABBREVIATION = 'UE';

/** Codici a due lettere degli Stati membri ammessi dalla parte B punto 6. */
export const IDENTIFICATION_MARK_MEMBER_STATE_CODES = [
  'BE',
  'BG',
  'CZ',
  'DK',
  'DE',
  'EE',
  'GR',
  'ES',
  'FR',
  'HR',
  'IE',
  'IT',
  'CY',
  'LV',
  'LT',
  'LU',
  'HU',
  'MT',
  'NL',
  'AT',
  'PL',
  'PT',
  'SI',
  'SK',
  'FI',
  'RO',
  'SE',
] as const;

/** Rapporto larghezza/altezza dell'ovale disegnato. Scelta di prodotto. */
export const IDENTIFICATION_MARK_ASPECT_RATIO = 1.45;

export interface PrepackagedIdentificationMark {
  countryCode: string;
  approvalNumber: string;
}

/** Le tre righe centrate dentro l'ovale, dall'alto in basso: paese, numero di
 *  riconoscimento, sigla dell'Unione (parte B punti 6-8). */
export function identificationMarkLines(
  mark: PrepackagedIdentificationMark,
): [string, string, string] {
  return [mark.countryCode, mark.approvalNumber, IDENTIFICATION_MARK_PRINTED_ABBREVIATION];
}

const MARK_TEXT_LINES = 3;
const MARK_LINE_HEIGHT_RATIO = 1.2;
/**
 * Quanto delle tre righe di testo l'ovale deve coprire in altezza perche' non
 * tocchino il bordo: un rettangolo inscritto in un'ellisse la riempie al
 * massimo per un fattore sqrt(2) ~ 0.707 in ogni dimensione; questo margine
 * resta sotto quel limite. Scelta di prodotto, non un rapporto di legge.
 */
const MARK_VERTICAL_FILL_RATIO = 0.65;

/**
 * Il pavimento di leggibilita' del blocco, dato l'x-height minimo dichiarato
 * per l'etichetta. Sotto questa dimensione il marchio non stampa: vedi il
 * commento in testa al file — non e' una misura che il regolamento impone,
 * e' la soglia sotto la quale Prospero rifiuta di stampare un ovale che le
 * tre righe non possono riempire in modo leggibile.
 */
export function identificationMarkFloorMm(minXHeightMm: number): {
  minWidthMm: number;
  minHeightMm: number;
} {
  const fontSizeMm = minXHeightMm / PRINT_FONT_X_HEIGHT_RATIO;
  const textHeightMm = MARK_TEXT_LINES * fontSizeMm * MARK_LINE_HEIGHT_RATIO;
  const minHeightMm = textHeightMm / MARK_VERTICAL_FILL_RATIO;
  const minWidthMm = minHeightMm * IDENTIFICATION_MARK_ASPECT_RATIO;
  return { minWidthMm, minHeightMm };
}

const MEMBER_STATE_CODE_SET = new Set<string>(IDENTIFICATION_MARK_MEMBER_STATE_CODES);

/** Vero quando il codice paese e' uno di quelli ammessi. */
export function isAcceptedMemberStateCode(value: string): boolean {
  return MEMBER_STATE_CODE_SET.has(value);
}

/**
 * Debole per scelta, come `isReachableOsaAddress`: il formato del numero di
 * riconoscimento varia per Stato membro e questo codice non li conosce tutti.
 * Rifiuta cio' che palesemente non e' un numero di riconoscimento e alimenta
 * una SUGGESTION, mai un rifiuto. Il pavimento e' 1, non 2: il registro
 * ministeriale (open data STAB_POA) elenca stabilimenti reali e noti con
 * numeri a una cifra (es. «UE IT 5», Bolton Food).
 */
export function isWellFormedApprovalNumber(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= 20 && /^[A-Z0-9 /.-]+$/.test(trimmed);
}
